import { BindAll } from 'lodash-decorators'
import { action, computed, observable } from 'mobx'
import moment from 'moment'
import { $service } from '@xt/client/experimental/request/service'
import { EnumContentType, EnumContentTypeText, EnumProductType, EnumProductUnit } from '@xt/client/enums/product'
import { EnumApiProduct } from '@xt/client/api'
import { chunk, divide, toFinite } from 'lodash'

import { AjaxBasics } from '../../helpers/ajaxBasics'
import { getFieldParser, matchOldVersion } from './_utils'
import { cacheManager } from './_cache-v2'

import type { CourseButton } from '@xt/client/types/home.d'
import type { OriginProductItem, SearchForm, PageStatus, InfoText } from './types.d'
import type { Dictionary } from 'vue-router/types/router'

export enum EnumLoadProductListResult {
  /**
   * 异常取消
   */
  Cancel,
  /**
   * 接口调用失败
   */
  Error,
  /**
   * 数据加载成功
   */
  Loaded,
  /**
   * 没有下一页数据了
   */
  Complete
}

@BindAll()
export class ControllerProductV2 {
  private static PageSize = 10

  // 防止竞态
  private requestTime: number = 0

  /**
   * 当前查询条件下的状态
   */
  @observable status: PageStatus = {
    page: 0,
    skeletonLoading: false,
    loading: false,
    hasMore: true
  }

  @observable rawList: OriginProductItem[] = []

  @computed get listPC(): OriginProductItem[][] {
    return chunk(this.rawList, 4)
  }

  public getTipLabel(contentType: EnumContentType): string {
    switch (contentType) {
      case EnumContentType.Painting:
        return EnumContentTypeText.Painting
      case EnumContentType.Calligraphy:
        return EnumContentTypeText.Calligraphy
      case EnumContentType.CalligraphyAndReading:
        return EnumContentTypeText.CalligraphyAndReading
      case EnumContentType.Health:
        return EnumContentTypeText.Health
      case EnumContentType.Physical:
        return EnumContentTypeText.Physical
      case EnumContentType.Reading:
        return EnumContentTypeText.Reading
      case EnumContentType.Writing:
        return EnumContentTypeText.Writing
      case EnumContentType.AestheticEducation:
        return EnumContentTypeText.AestheticEducation
      default:
        return ''
    }
  }

  /**
   * 价格，课程进度信息映射
   * @param productItem 原始商品数据
   * @returns 价格，课程进度信息数据
   */
  public mapInfoText(data: OriginProductItem): InfoText {
    const infoText: InfoText = {
      price: 0,
      bizProdCount: data.bizProdCount,
      unit: ''
    }

    if (data.discountPrice && data.discountPrice !== 0) {
      infoText.price = divide(toFinite(data.discountPrice), 100)
    }

    if (data.productType === EnumProductType.single || data.productType === EnumProductType.fullstage) {
      infoText.unit = EnumProductUnit.LiveCourseCountUnit
    } else if (data.productType === EnumProductType.onlineExhibition || data.productType === EnumProductType.offlineExhibition) {
      infoText.unit = EnumProductUnit.ExhibitonCourseCountUnit
      infoText.bizProdCount = infoText.bizProdCount + 2
    } else if (data.productType === EnumProductType.dayCourse) {
      infoText.unit = EnumProductUnit.DayCourseCountUnit
    } else {
      infoText.unit = EnumProductUnit.PhysicalCountUnit
    }

    return infoText
  }

  /**
   * 校验页面的query参数并格式化 兼容旧版本
   * @param routerQuery 页面query参数
   */
  public formatSearchForm(routerQuery: Dictionary<string | (string | null)[]>): SearchForm {
    // 兼容旧版本
    if (typeof routerQuery['typeCurrent'] === 'string' && typeof routerQuery['directionCurrent'] === 'string') {
      return matchOldVersion({ typeCurrent: routerQuery['typeCurrent'], directionCurrent: routerQuery['directionCurrent'] })
    }

    const parseField = getFieldParser(routerQuery)

    return {
      contentType: parseField('contentType'),
      learnTimeYear: parseField('learnTimeYear'),
      orderType: parseField('orderType'),
      productType: parseField('productType'),
      studyProgress: parseField('studyProgress'),
      studyWay: parseField('studyWay')
    }
  }

  public toSearchQuery(form: SearchForm): Record<keyof SearchForm, string> {
    const obj = {} as Record<keyof SearchForm, string>

    Object.keys(form).forEach(key => {
      if (form[key].toString() !== '') {
        obj[key] = form[key].toString()
      }
    })

    return obj
  }

  /**
   * 按钮映射
   * @param productItem 原始商品数据
   * @returns 按钮组件数据
   */
  public mapCourseButton(productItem: OriginProductItem): null | CourseButton {
    const now = AjaxBasics.serviceDate

    const { signUpEndTime, signUpStartTime, learnStartTime, learnEndTime, turnPkgEndTime } = productItem

    const startTimeMoment = moment(learnStartTime)
    const endTimeMoment = moment(learnEndTime)
    const signUpStartTimeMoment = moment(signUpStartTime)
    const signUpEndTimeMoment = moment(signUpEndTime)

    // 随报随学
    if (signUpEndTime === 2147000000000) {
      return {
        type: 'normal',
        label: '随报随学',
        className: 'style-yellow'
      }
    }

    if (productItem.canSignUp === false) {
      return {
        type: 'normal',
        label: '报名未开始',
        className: 'style-gray'
      }
    }

    // 还没有到报名开始时间
    if (now.isBefore(signUpStartTimeMoment)) {
      return {
        type: 'normal',
        label: signUpStartTimeMoment.utcOffset(+8).format('MM月DD日报名'),
        className: 'style-yellow'
      }
    }

    // 报名已开始 且 报名还没有结束
    if (now.isAfter(signUpStartTimeMoment) && now.isBefore(signUpEndTimeMoment)) {
      const daysDiff = signUpEndTimeMoment.diff(now, 'days')
      const hoursDiff = signUpEndTimeMoment.diff(now, 'hours')
      if (hoursDiff > 24) {
        // case 1: 距报名结束大于24小时
        return {
          type: 'normal',
          label: `${daysDiff}天后报名结束`,
          className: 'style-red'
        }
      } else {
        // case 2: 距报名结束不足24小时
        return {
          type: 'countdown',
          suffix: '后报名结束',
          endTime: signUpEndTimeMoment.valueOf(),
          className: 'style-red'
        }
      }
    }

    // 全阶课 有转报时间，转报时间没有结束case
    if (turnPkgEndTime && productItem.productType === EnumProductType.fullstage) {
      const convertEndTimeMoment = moment(turnPkgEndTime)

      if (now.isBefore(convertEndTimeMoment)) {
        const daysDiff = convertEndTimeMoment.diff(now, 'days')
        const hoursDiff = convertEndTimeMoment.diff(now, 'hours')

        if (hoursDiff > 24) {
          // case 1: 距转报结束大于24小时
          return {
            type: 'normal',
            label: `${daysDiff}天后转报结束`,
            className: 'style-red'
          }
        } else {
          // case2: 距转报结束不足24小时
          return {
            type: 'countdown',
            suffix: '后转报结束',
            endTime: convertEndTimeMoment.valueOf(),
            className: 'style-red'
          }
        }
      }
    }

    // 报名已经结束了 但是还没有开课
    if (now.isAfter(signUpEndTimeMoment) && now.isBefore(startTimeMoment)) {
      return {
        type: 'normal',
        label: '报名已结束',
        className: 'style-gray'
      }
    }

    const isShow = [EnumProductType.onlineExhibition, EnumProductType.offlineExhibition].includes(productItem.productType)

    // 展览课程需要独立处理 投票时间
    if (isShow) {
      const voteStartTimeMoment = moment(productItem.voteStartTime)
      const voteEndTimeMoment = moment(productItem.voteEndTime)
      const showStartTimeMoment = moment(productItem.readyStartTime)
      const showEndTimeMoment = moment(productItem.readyEndTime)

      if (now.isAfter(showStartTimeMoment) && now.isBefore(showEndTimeMoment)) {
        return {
          type: 'normal',
          label: '备展中',
          className: 'style-yellow'
        }
      } else if (now.isAfter(showEndTimeMoment) && now.isBefore(voteStartTimeMoment)) {
        return {
          type: 'normal',
          label: '备展结束',
          className: 'style-gray'
        }
      } else if (now.isAfter(voteStartTimeMoment) && now.isBefore(voteEndTimeMoment)) {
        return {
          type: 'normal',
          label: '投票中',
          className: 'style-red'
        }
      }

      return {
        type: 'normal',
        label: '观展',
        className: 'style-yellow'
      }
    }

    if (now.isAfter(startTimeMoment) && now.isBefore(endTimeMoment)) {
      return {
        type: 'normal',
        label: '开课中',
        className: 'style-yellow'
      }
    }

    if (now.isAfter(endTimeMoment)) {
      return {
        type: 'normal',
        label: '已结课',
        className: 'style-gray'
      }
    }

    return null
  }

  /**
   * 唯一请求数据方法，如果有缓存则直接取缓存数据 否则发起请求
   * @param form 当前表单数据
   */
  public async loadProductList(form: SearchForm): Promise<EnumLoadProductListResult> {
    const { loading, page, hasMore } = this.status

    if (loading) {
      return EnumLoadProductListResult.Cancel
    }

    if (!hasMore) {
      return EnumLoadProductListResult.Cancel
    }

    const nextPage = page + 1

    // 检查缓存
    const cacheData = cacheManager.getCache(form, nextPage)

    const shouldRequest = cacheData === null || cacheData.expired === true

    // 如果缓存已经确认失效 清除掉旧数据
    if (cacheData?.expired === true) {
      console.warn('缓存已经确认失效，清理旧数据 重新请求第一页数据！')
      cacheManager.removeItem(form)
      this.resetStatus()
    }

    // 重新请求数据
    if (shouldRequest) {
      this.updatePageStatus({ loading: true, skeletonLoading: nextPage === 1 })
      const [result, requestTime] = await this.fetchProductList(form)
      this.updatePageStatus({ loading: false, skeletonLoading: false })

      if (requestTime !== this.requestTime) {
        console.warn('命中竞态')
        return EnumLoadProductListResult.Cancel
      }

      if (result === null) {
        return EnumLoadProductListResult.Error
      }

      const hasMore = result.length === ControllerProductV2.PageSize
      this.appendRawList(result)
      this.updatePageStatus({ page: nextPage, hasMore })
      cacheManager.setCache(result, { form, page: nextPage, hasMore })
      return hasMore ? EnumLoadProductListResult.Loaded : EnumLoadProductListResult.Complete
    }

    // 缓存数据可用
    const { hasMore: cacheHasMore, data } = cacheData
    console.warn(`命中缓存数据 hasMore = ${cacheHasMore} page = ${nextPage} len = ${data.length}`)
    this.updatePageStatus({ page: nextPage })
    this.appendRawList(data)
    return cacheHasMore ? EnumLoadProductListResult.Loaded : EnumLoadProductListResult.Complete
  }

  private async fetchProductList(form: SearchForm): Promise<[null | Array<OriginProductItem>, number]> {
    const now = Date.now()
    this.requestTime = now
    const nextPage = this.status.page + 1

    const [, result] = await $service<Array<OriginProductItem>>({
      url: EnumApiProduct.ProductListV2,
      method: 'GET',
      params: {
        pageSize: ControllerProductV2.PageSize,
        page: nextPage,
        ...form
      }
    })

    return [result, now]
  }

  public clean() {
    this.resetStatus()
    cacheManager.clear()
  }

  public resetStatus() {
    this.updateRawList([])
    this.updatePageStatus({ page: 0, skeletonLoading: false, loading: false, hasMore: true })
  }

  @action
  private updateRawList(list: OriginProductItem[]) {
    this.rawList = list
  }

  @action
  private appendRawList(list: OriginProductItem[]) {
    this.rawList = [...this.rawList, ...list]
  }

  @action
  private updatePageStatus(status: Partial<PageStatus>) {
    this.status = {
      ...this.status,
      ...status
    }
  }
}
